import { useCallback, useState } from 'react';
import { BackButton } from '../../components/BackButton';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { StickyFooter } from '../../components/StickyFooter';
import { TopBar } from '../../components/TopBar';
import { ExtrasCard } from '../../composites/ExtrasCard';
import { GuestSelectModal } from '../../composites/GuestSelectModal';
import type { GuestOption } from '../../composites/GuestSelectModal/GuestSelectModal.types';
import type { ExtrasTemplateProps } from './ExtrasTemplate.types';

export function ExtrasTemplate({
  items,
  imageAspectRatio = '16 / 9',
  totalAmount,
  originalAmount,
  currencyCode,
  locale,
  guests,
  onBack,
  onAdd,
  onRemove,
  onConfirmGuestSelection,
  onCtaClick,
  ctaDisabled,
  labels,
}: ExtrasTemplateProps) {
  const {
    title,
    subtitle,
    backAriaLabel,
    add,
    remove,
    quantity,
    editSelections,
    noImage,
    footerTitle,
    footerTotal,
    ctaLabel,
    modalTitle,
    modalConfirm,
    modalClose,
    modalTotal,
  } = labels;

  const isMultiGuest = (guests?.length ?? 0) > 1;

  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [modalGuests, setModalGuests] = useState<GuestOption[]>([]);

  const openModal = useCallback(
    (itemId: string, preselectedIds?: string[]) => {
      if (!guests) return;
      const item = items.find((i) => i.id === itemId);
      if (!item) return;

      const guestOptions: GuestOption[] = guests.map((guest) => ({
        id: guest.id,
        label: guest.label,
        selected: preselectedIds ? preselectedIds.includes(guest.id) : true,
        price: { amount: item.price.amount, currencyCode: item.price.currencyCode },
      }));

      setModalGuests(guestOptions);
      setModalItemId(itemId);
    },
    [guests, items]
  );

  const handleToggleModalGuest = useCallback((guestId: string) => {
    setModalGuests((prev) =>
      prev.map((g) => (g.id === guestId ? { ...g, selected: !g.selected } : g))
    );
  }, []);

  const handleConfirmModal = useCallback(() => {
    if (!modalItemId || !onConfirmGuestSelection) return;
    const selectedIds = modalGuests.filter((g) => g.selected).map((g) => g.id);
    onConfirmGuestSelection(modalItemId, selectedIds);
    setModalItemId(null);
    setModalGuests([]);
  }, [modalItemId, modalGuests, onConfirmGuestSelection]);

  const handleCloseModal = useCallback(() => {
    setModalItemId(null);
    setModalGuests([]);
  }, []);

  const handleAdd = useCallback(
    (itemId: string) => {
      if (isMultiGuest) {
        openModal(itemId);
      } else {
        onAdd(itemId);
      }
    },
    [isMultiGuest, openModal, onAdd]
  );

  const handleEditSelections = useCallback(
    (itemId: string) => {
      const item = items.find((i) => i.id === itemId);
      if (!item || !item.selectedGuestIds) return;
      openModal(itemId, item.selectedGuestIds);
    },
    [items, openModal]
  );

  const modalItem = modalItemId ? items.find((i) => i.id === modalItemId) : null;

  return (
    <>
      {onBack && (
        <TopBar $borderless>
          <BackButton onClick={onBack} ariaLabel={backAriaLabel} />
        </TopBar>
      )}

      <PageSection>
        <PageHeading title={title} subtitle={subtitle} />

        <ContentArea>
          <CardGrid $columns={3}>
            {items.map((item) => (
              <ExtrasCard
                key={item.id}
                image={item.image}
                imageAspectRatio={imageAspectRatio}
                title={item.title}
                price={{ ...item.price, locale }}
                selected={item.selected}
                quantity={item.quantity}
                onAdd={() => handleAdd(item.id)}
                onRemove={() => onRemove(item.id)}
                onEditSelections={
                  isMultiGuest && item.selected ? () => handleEditSelections(item.id) : undefined
                }
                labels={{ add, remove, quantity, editSelections, noImage }}
              />
            ))}
          </CardGrid>
        </ContentArea>
      </PageSection>
      <StickyFooter
        title={footerTitle}
        totalLabel={footerTotal}
        totalAmount={totalAmount}
        originalAmount={originalAmount}
        currencyCode={currencyCode}
        locale={locale}
        ctaLabel={ctaLabel}
        onCtaClick={onCtaClick}
        ctaDisabled={ctaDisabled}
        align="right"
      />
      {isMultiGuest && (
        <GuestSelectModal
          open={modalItemId !== null}
          onClose={handleCloseModal}
          guests={modalGuests}
          onToggleGuest={handleToggleModalGuest}
          onConfirm={handleConfirmModal}
          extraTitle={modalItem?.title}
          currencyCode={currencyCode}
          locale={locale}
          labels={{
            title: modalTitle,
            confirm: modalConfirm,
            close: modalClose,
            total: modalTotal,
          }}
        />
      )}
    </>
  );
}
